import { ROLES } from 'config/roles.config'
// components/UpdateComponents/ConnectedUsersList.tsx
import CloseIcon from '@mui/icons-material/Close'
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Modal,
  Typography,
  useTheme,
} from '@mui/material'
import { useUserItems } from 'api/aws/users/useUserItems'
import React, { useEffect, useMemo, useState } from 'react'
import { BoxScroller } from 'styles/elements/styleElements'
import { useActiveUser } from '../../api/slack/useActiveUser'
import SendBroadcast from '../../context/Websocket/SendBroadcast'
import { useWebsocketContext } from '../../context/Websocket/WebsocketProvider'

const adminUsers = [...ROLES.ADMIN_IDS]

const formatElapsed = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

const ConnectedUsersListContent: React.FC<{ users: any[]; isAdmin?: boolean }> = ({ users, isAdmin }) => {
  const { connectedUsers } = useWebsocketContext()
  // tick every second to update "connected for" labels
  const [, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Merge Slack users (avatars/names) with WS connections by id
  const merged = useMemo(() => {
    return (connectedUsers ?? [])
      .map(cu => {
        const slack = users?.find((u: any) => u.user_uuid === cu.id)
        return {
          id: cu.id,
          name:
            slack?.name ||
            slack?.real_name ||
            cu.name ||
            `${cu.firstName ?? ''} ${cu.lastName ?? ''}`.trim() ||
            'Unknown',
          image_512: slack?.image_512 || slack?.profile?.image_512 || slack?.profile?.image_192 || '',
          connectedAt: Number(cu.connectedAt), // 👈 ensure it's a number
        }
      })
      .sort((a, b) => a.connectedAt - b.connectedAt)
  }, [connectedUsers, users])

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="subtitle2" sx={{ opacity: 0.75 }}>
          Currently connected:
        </Typography>
        <Badge color="primary" sx={{ ml: 1 }} badgeContent={merged?.length || 0} />
      </Box>

      <List>
        {merged && merged.length > 0 ? (
          merged.map(u => (
            <React.Fragment key={u?.id}>
              <ListItem key={u.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar src={u.image_512} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{u.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connected for: {formatElapsed(Date.now() - u.connectedAt)}
                  </Typography>
                </Box>
              </ListItem>
            </React.Fragment>
          ))
        ) : (
          <ListItem>
            <Typography variant="body2">No users connected</Typography>
          </ListItem>
        )}
      </List>
    </>
  )
}

const ConnectedUsersList: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const theme = useTheme()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { activeUser } = useActiveUser()
  const isAdmin = useMemo(() => {
    if (!activeUser || isUsersLoading || !users) return false
    return (activeUser?.user && adminUsers.includes(activeUser.user.id)) || false
  }, [activeUser])
  return (
    <Modal open={isOpen} onClose={onClose}>
      <>
        <BoxScroller
          sx={{
            position: 'absolute',
            top: '39%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            height: '100%',
            maxHeight: isAdmin ? '65vh' : '85vh',
            overflowY: 'auto',
            bgcolor: theme.palette.background.paper,
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: 'none',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Connected Users</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {isUsersLoading ? <CircularProgress /> : <ConnectedUsersListContent users={users || []} isAdmin={isAdmin} />}
        </BoxScroller>
        {isAdmin && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '0%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 520,
              overflowY: 'auto',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              outline: 'none',
              backdropFilter: 'blur(10px)',
              mt: theme.spacing(2),
            }}
          >
            <SendBroadcast />
          </Box>
        )}
      </>
    </Modal>
  )
}

export default ConnectedUsersList

// import React, { useMemo, useState } from 'react'
// import {
//   Box,
//   Badge,
//   Typography,
//   Fab,
//   Modal,
//   Avatar,
//   IconButton,
//   useTheme,
//   Divider,
//   List,
//   ListItem,
//   CircularProgress,
//   Drawer,
//   Button,
// } from '@mui/material'
// import { useWebsocketContext } from '../../context/Websocket/WebsocketProvider'
// import CloseIcon from '@mui/icons-material/Close'
// import PeopleIcon from '@mui/icons-material/People'
// import SendBroadcast from '../../context/Websocket/SendBroadcast'
// import moment from 'moment'
// import { useActiveUser } from '../../api/slack/useActiveUser'
// import { useUserItems } from 'api/aws/users/useUserItems'
// import { useUsersInfo } from 'api/slack/useUsersInfo'
// const adminUsers = [...ROLES.ADMIN_IDS]

// const ConnectedUsersList = ({ isOpen, onClose }) => {
//   const theme = useTheme()
//   const { data: users, isLoading: isUsersLoading } = useUserItems()
//   const { activeUser } = useActiveUser()
//   const { userInfo } = useUsersInfo()
//   console.log('userInfo', userInfo)
//   // const [open, setOpen] = useState(false)
//   // const fetchUserInfo = () => {
//   //   getUserInfo()
//   // }
//   return (
//     <>
//       <Modal open={isOpen} onClose={onClose}>
//         {/* <Button onClick={fetchUserInfo}></Button> */}
//         <Box
//           sx={{
//             position: 'absolute',
//             top: '50%',
//             left: '50%',
//             transform: 'translate(-50%, -50%)',
//             width: 400,
//             height: '85vh',
//             maxHeight: '85vh',
//             overflowY: 'auto',
//             bgcolor: theme.palette.background.paper,
//             boxShadow: 24,
//             p: 4,
//             borderRadius: 2,
//             outline: 'none',
//           }}
//         >
//           <Box
//             sx={{
//               display: 'flex',
//               justifyContent: 'space-between',
//               alignItems: 'center',
//               mb: 2,
//             }}
//           >
//             <Typography variant="h6">Connected Users</Typography>

//             <IconButton onClick={onClose}>
//               <CloseIcon />
//             </IconButton>
//           </Box>
//           <Divider sx={{ mb: 2 }} />
//           {isUsersLoading ? <CircularProgress /> : <ConnectedUsersListContent users={users} />}
//           <Box sx={{ marginTop: theme.spacing(9) }}>
//             {activeUser && activeUser.user && adminUsers.includes(activeUser.user.id) && <SendBroadcast />}
//           </Box>
//         </Box>
//       </Modal>
//     </>
//   )
// }
// // const ConnectedUsersList = ({ isOpen, onClose }) => {
// //   const theme = useTheme()
// //   const { data: users, isLoading: isUsersLoading } = useUserItems()
// //   const { activeUser } = useActiveUser()
// //   const { connectedUsers } = useWebsocketContext() // Accessing connectedUsers directly here
// //   // const [open, setOpen] = useState(false)

// //   return (
// //     <>
// //       <Drawer
// //         open={isOpen}
// //         onClose={onClose}
// //         anchor="right"
// //         SlideProps={{
// //           easing: {
// //             enter: '2',
// //             exit: '3',
// //           },
// //         }}
// //       >
// //         <Box
// //           sx={
// //             {
// //               // position: 'absolute',
// //               // top: '50%',
// //               // left: '50%',
// //               // transform: 'translate(-50%, -50%)',
// //               // width: 400,
// //               // height: '85vh',
// //               // maxHeight: '85vh',
// //               // overflowY: 'auto',
// //               // bgcolor: theme.palette.background.paper,
// //               // boxShadow: 24,
// //               // p: 4,
// //               // borderRadius: 2,
// //               // outline: 'none',
// //             }
// //           }
// //         >
// //           <Box
// //             sx={{
// //               display: 'flex',
// //               justifyContent: 'space-between',
// //               alignItems: 'center',
// //               mb: 2,
// //             }}
// //           >
// //             <Typography variant="h6">Connected Users</Typography>

// //             <IconButton onClick={onClose}>
// //               <CloseIcon />
// //             </IconButton>
// //           </Box>
// //           <Divider sx={{ mb: 2 }} />
// //           {isUsersLoading ? (
// //             <CircularProgress />
// //           ) : (
// //             <ConnectedUsersListContent users={users} connectedUsers={connectedUsers} />
// //           )}
// //           <Box sx={{ marginTop: theme.spacing(9) }}>
// //             {activeUser && activeUser.user && adminUsers.includes(activeUser.user.id) && <SendBroadcast />}
// //           </Box>
// //         </Box>
// //       </Drawer>
// //     </>
// //   )
// // }

// const ConnectedUsersListContent = ({ users }) => {
//   const { connectedUsers } = useWebsocketContext()
//   // const connectedUserDetails = useMemo(() => {
//   //   return connectedUsers.map(connectedUser => {
//   //     const userDetail = users.find(user => user.id === connectedUser.id)
//   //     return { ...userDetail, ...connectedUser }
//   //   })
//   // }, [connectedUsers, users])
// const connectedUserDetails = useMemo(() => {
//   return connectedUsers?.map(connectedUser => {
//     const userDetail = users.find(user => user.id === connectedUser.id)
//     return { ...userDetail, ...connectedUser }
//   })
//   // return connectedUsers
// }, [connectedUsers])

//   function formatSessionTime(ms) {
//     const totalSeconds = Math.floor(ms / 1000)
//     const hours = Math.floor(totalSeconds / 3600)
//     const minutes = Math.floor((totalSeconds % 3600) / 60)
//     const seconds = totalSeconds % 60

//     return `${hours}h ${minutes}m ${seconds}s`
//   }

//   return (
//     <List>
//       {connectedUserDetails && connectedUserDetails.length > 0 ? (
//         connectedUserDetails.map(user =>
//           user ? (
//             <ListItem key={user.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//               <Avatar src={user.image_512} sx={{ marginRight: 2, height: 75, width: 75 }} />
//               <Box sx={{ flexGrow: 1 }}>
//                 <Typography>{user.name}</Typography>
//                 <Typography variant="body2" color="text.secondary">
//                   ID: {user.id}
//                 </Typography>
//                 <Typography variant="body2" color="text.secondary">
//                   Session Time: {formatSessionTime(user.sessionTime)}
//                 </Typography>
//                 <Typography variant="body2" color="text.secondary">
//                   Last Ping: {moment(user.lastPing).fromNow()}
//                 </Typography>
//                 <Typography variant="body2" color="text.secondary">
//                   Pong Count: {user.pongCount}
//                 </Typography>
//               </Box>
//             </ListItem>
//           ) : (
//             <ListItem key={`unknown-${Math.random()}`}>
//               <Typography variant="body2">Unknown User Connected</Typography>
//             </ListItem>
//           )
//         )
//       ) : (
//         <ListItem>
//           <Typography variant="body2">No users connected</Typography>
//         </ListItem>
//       )}
//     </List>
//   )
// }

// export default ConnectedUsersList
